
import { defineComponent, ref, computed, watch, onMounted } from "vue";
import ApiServiceCustom from "@/core/services/ApiServiceCustom";
import { AxiosResponse } from "axios";

interface ParametrosAnoData {
  id: number;
  ano_atual: number;
  ano_matricula: number;
  ano_matricula_ant: number;
  ano_planejamento: number;
  dia_base: number;
  mes_base: number;
  data_inclusao: number;
  usuario_inclusao: string;
  data_alteracao: number;
  usuario_alteracao: string;
  situacao: string;
}

export default defineComponent({
  name: "ParametrosAno",
  props: {
    name: {
      type: String,
      required: true,
      validator: (value: string) => {
        return [
          "ano_atual",
          "ano_matricula",
          "ano_matricula_ant",
          "ano_planejamento",
        ].includes(value);
      },
    },
    modelValue: {
      type: [String, Number],
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    label: {
      type: String,
      default: "", // Novo prop para label personalizado
    },
    placeholder: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: true, // Por padrão é readonly
    },
    maxLength: {
      type: Number,
      default: 4,
    },
    pattern: {
      type: String,
      default: "",
    },
    autoFetch: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["update:modelValue", "change", "parametrosLoaded", "blur", "focus"],
  setup(props, { emit }) {
    const isLoading = ref(false);
    const parametros = ref<ParametrosAnoData | null>(null);
    const isTouched = ref(false);
    const isFocused = ref(false);
    const errorMessage = ref("");
    const userModified = ref(false); // Flag para saber se usuário modificou o valor

    // Mapeamento de names para labels
    const labelMapping = {
      ano_atual: "Ano Atual:",
      ano_matricula: "Ano da Matrícula:",
      ano_matricula_ant: "Ano da Matrícula Anterior:",
      ano_planejamento: "Ano do Planejamento:",
      ano_base: "Ano Base:",
    };

    // Mapeamento de names para placeholders padrão
    const placeholderMapping = {
      ano_atual: "Ex: 2025",
      ano_matricula: "Ex: 2025",
      ano_matricula_ant: "Ex: 2024",
      ano_planejamento: "Ex: 2026",
      ano_base: "Ex: 2025",
    };

    // Computed para label - prioriza o prop label se fornecido
    const labelText = computed(() => {
      // Se o prop label foi informado, usa ele
      if (props.label && props.label.trim() !== "") {
        return props.label;
      }

      // Caso contrário, usa o mapeamento baseado no name
      return (
        labelMapping[props.name as keyof typeof labelMapping] || props.name
      );
    });

    const placeholderText = computed(() => {
      return (
        props.placeholder ||
        placeholderMapping[props.name as keyof typeof placeholderMapping] ||
        ""
      );
    });

    const displayValue = computed(() => {
      if (isLoading.value) return "";

      // Se autoFetch está desabilitado (modo edição), sempre usa o modelValue
      if (!props.autoFetch) {
        return props.modelValue;
      }

      // Se está desabilitado, sempre usa valor da API
      if (props.disabled) {
        if (parametros.value && props.name in parametros.value) {
          return parametros.value[props.name as keyof ParametrosAnoData];
        }
      }

      // Se está habilitado e usuário modificou, usa o modelValue
      if (!props.disabled && userModified.value) {
        return props.modelValue;
      }

      // Se está habilitado mas usuário não modificou ainda, usa valor da API se disponível
      if (
        !props.disabled &&
        !userModified.value &&
        parametros.value &&
        props.name in parametros.value
      ) {
        return parametros.value[props.name as keyof ParametrosAnoData];
      }

      // Fallback para modelValue
      return props.modelValue;
    });

    const isValid = computed(() => {
      return isTouched.value && !props.required && displayValue.value;
    });

    const isInvalid = computed(() => {
      if (!isTouched.value) return false;
      if (props.required && !displayValue.value) {
        return true;
      }
      return false;
    });

    // Corrige o erro de efeito colateral em computed
    watch([isInvalid, isTouched, displayValue], () => {
      if (isInvalid.value) {
        errorMessage.value = "Este campo é obrigatório.";
      } else {
        errorMessage.value = "";
      }
    });

    // Watch para resetar userModified quando disabled muda para true
    watch(
      () => props.disabled,
      (newDisabled) => {
        if (newDisabled) {
          userModified.value = false;
        }
      }
    );

    // Watch no modelValue para atualizar quando vem do pai (caso de edição)
    watch(
      () => props.modelValue,
      (newValue) => {
        // Se autoFetch está desabilitado e recebemos um novo valor, não marca como userModified
        if (!props.autoFetch && newValue) {
          userModified.value = false;
        }
      }
    );

    watch(
      () => props.modelValue,
      (newValue) => {
        if (newValue === "" || newValue === null) {
          userModified.value = false;
          parametros.value = null; // zera cache interno
        }
      }
    );

    // Função para buscar parâmetros
    const pegarParametrosAno = async () => {
      try {
        isLoading.value = true;
        const {
          data: { dados },
        }: AxiosResponse<any> = await ApiServiceCustom.get({
          defaultUrl: process.env.VUE_APP_MATRICULA_API,
          resource: `parametros_ano`,
        });

        if (dados && dados.length > 0) {
          parametros.value = dados[0];

          // Só atualiza o modelValue se o usuário não tiver modificado ainda
          if (!userModified.value) {
            let fieldValue: string | number | null = null;
            if (parametros.value) {
              fieldValue =
                parametros.value[props.name as keyof ParametrosAnoData];
            }
            emit("update:modelValue", fieldValue);
            emit("change", { target: { value: fieldValue } });
          }

          // Emite evento para o pai saber que os parâmetros foram carregados
          emit("parametrosLoaded", parametros.value);
        }
      } catch (error) {
        parametros.value = null;
        errorMessage.value = "Erro ao carregar dados.";
      } finally {
        isLoading.value = false;
      }
    };

    // Busca automática na montagem do componente
    onMounted(() => {
      if (props.autoFetch) {
        pegarParametrosAno();
      }
    });

    // Função para permitir apenas números nas teclas
    const handleKeydown = (event: KeyboardEvent) => {
      // Permite: backspace, delete, tab, escape, enter, home, end, setas
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "Home",
        "End",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
      ];

      // Permite Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      if (
        event.ctrlKey &&
        ["a", "c", "v", "x", "z"].includes(event.key.toLowerCase())
      ) {
        return;
      }

      // Se não é uma tecla permitida e não é um número, cancela o evento
      if (!allowedKeys.includes(event.key) && !/^[0-9]$/.test(event.key)) {
        event.preventDefault();
      }
    };

    const handleInput = (event: Event) => {
      const target = event.target as HTMLInputElement;

      // Remove caracteres não numéricos
      let value = target.value.replace(/[^0-9]/g, "");

      // Atualiza o valor do input se foi modificado
      if (value !== target.value) {
        target.value = value;
      }

      userModified.value = true; // Marca que usuário modificou
      emit("update:modelValue", value);
      emit("change", { ...event, target: { ...target, value } });
    };

    const handleBlur = (event: Event) => {
      isTouched.value = true;
      isFocused.value = false;
      emit("blur", event);
    };

    const handleFocus = (event: Event) => {
      isFocused.value = true;
      emit("focus", event);
    };

    return {
      isLoading,
      labelText,
      placeholderText,
      displayValue,
      isValid,
      isInvalid,
      errorMessage,
      handleInput,
      handleKeydown,
      handleBlur,
      handleFocus,
      pegarParametrosAno, // Expõe para uso externo se necessário
    };
  },
});
